import React, { useState, useEffect, useRef } from "react";
import "./Calender.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import { Visibility } from "@mui/icons-material";
import { message } from "antd";
import Spinner from "../../../components/common/Spinner";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CustomSelect from "../../../components/common/CustomSelect";
import ReactPlayer from "react-player";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ApiCallService from "../../../Service/ApiCallService";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import DuoIcon from "@mui/icons-material/Duo";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Badge } from "@mui/material";
import { usePermission } from "../../../permissions/usePermission";

const CALENDAR_FILTER_STORAGE_KEY = "vmsCalendarFilters";
const CALENDAR_YEAR_RANGE_OFFSET = 10;

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getStoredCalendarFilters = () => {
  try {
    return (
      JSON.parse(sessionStorage.getItem(CALENDAR_FILTER_STORAGE_KEY)) || {}
    );
  } catch (error) {
    return {};
  }
};

const updateStoredCalendarFilters = (updates) => {
  sessionStorage.setItem(
    CALENDAR_FILTER_STORAGE_KEY,
    JSON.stringify({
      ...getStoredCalendarFilters(),
      ...updates,
    }),
  );
};

const getStoredCalendarInitialDate = () => {
  const storedDate = getStoredCalendarFilters().activeDate;

  if (!storedDate || isNaN(new Date(storedDate))) {
    return undefined;
  }

  return storedDate;
};

const Calendar = () => {

  const navigate = useNavigate();
  const theme = useTheme();
  const calendarRef = useRef(null);
  const { can } = usePermission();
  const canCreateEvent =
    can("CALENDAR_MANAGEMENT", "CALENDAR_EVENTS", "create") ||
    can("CALENDAR", "", "create");
  const canViewEvent =
    can("CALENDAR_MANAGEMENT", "CALENDAR_EVENTS", "view") ||
    can("CORE", "CALENDAR", "view") ||
    can("CALENDAR", "", "view");
  const canListEvents =
    can("CALENDAR_MANAGEMENT", "CALENDAR_EVENTS", "list") || canViewEvent;
  const canEditEvent =
    can("CALENDAR_MANAGEMENT", "CALENDAR_EVENTS", "edit") ||
    can("CALENDAR", "", "edit");
  const canDeleteEvent =
    can("CALENDAR_MANAGEMENT", "CALENDAR_EVENTS", "delete") ||
    can("CALENDAR", "", "delete");
  const canDownloadCalendarData =
    can(
      "CALENDAR_MANAGEMENT",
      "CALENDAR_EVENTS",
      "download_calendar_data",
    ) || can("CALENDAR", "", "download");
  const canManageEvent = canEditEvent || canDeleteEvent;
  const canUseCalendarToolbar =
    canListEvents || canCreateEvent || canDownloadCalendarData;

  const [expanded, setExpanded] = useState(false);
  const [calenderTypes, setCalenderTypes] = useState([]);
  const [calendarSubTypes, setCalendarSubTypes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [selectedCalendarType, setSelectedCalendarType] = useState({});
  const [selectedCalendarSubType, setSelectedCalendarSubType] = useState({});

  const [allCalendarData, setAllCalendarData] = useState([]);
  const [calendarBackup, setCalendarBackup] = useState([]);

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [initialCalendarDate] = useState(getStoredCalendarInitialDate);
  const initialActiveDate = initialCalendarDate
    ? new Date(initialCalendarDate)
    : new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    isNaN(initialActiveDate)
      ? new Date().getMonth()
      : initialActiveDate.getMonth(),
  );
  const [selectedYear, setSelectedYear] = useState(
    isNaN(initialActiveDate)
      ? new Date().getFullYear()
      : initialActiveDate.getFullYear(),
  );
  const [activeCalendarPicker, setActiveCalendarPicker] = useState(null);

  const [IsManager, setIsManager] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const loggedInUserId = localStorage.getItem("userId");

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [likedEvents, setLikedEvents] = useState({});
  const [hasFetchedEvents, setHasFetchedEvents] = useState(false);
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState(null); // Track which event's tooltip is open

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const { control, setValue } = useForm({
    defaultValues: {
      calendarType: "all",
      calendarSubType: "all",
    },
  });

  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behaviour: "smooth" });
  }, []);

  useEffect(() => {
    if (canListEvents) {
      getCalenderTypes();
    }

    const location = window.location.pathname;

    if (
      location === "/networkManager/calendar" ||
      location === "/programManager/calendar"
    ) {
      setIsManager(true);
    } else {
      setIsManager(false);
    }
  }, [canListEvents]);

  useEffect(() => {
    if (canListEvents && startDate && endDate) {
      getCalenderData();
    }
  }, [
    canListEvents,
    startDate,
    endDate,
    selectedCalendarType,
    selectedCalendarSubType,
  ]);

  useEffect(() => {
    if (hasRestoredFilters || calenderTypes.length === 0) return;

    let isActive = true;

    const restoreStoredFilters = async () => {
      const storedFilters = getStoredCalendarFilters();
      const storedCalendarTypeId = storedFilters.calendarTypeId;

      if (!storedCalendarTypeId || storedCalendarTypeId === "all") {
        if (isActive) setHasRestoredFilters(true);
        return;
      }

      const storedCalendarType = calenderTypes.find(
        (type) => type._id === storedCalendarTypeId,
      );

      if (!storedCalendarType) {
        if (isActive) setHasRestoredFilters(true);
        return;
      }

      setSelectedCalendarType(storedCalendarType);
      setValue("calendarType", storedCalendarType._id);

      const subTypes = await fetchCalendarSub_Types(storedCalendarType._id);
      const storedCalendarSubTypeId = storedFilters.calendarSubTypeId;

      if (!isActive) return;

      if (storedCalendarSubTypeId && storedCalendarSubTypeId !== "all") {
        const storedCalendarSubType = subTypes.find(
          (subType) => subType._id === storedCalendarSubTypeId,
        );

        if (storedCalendarSubType) {
          setSelectedCalendarSubType(storedCalendarSubType);
          setValue("calendarSubType", storedCalendarSubType._id);
        }
      }

      setHasRestoredFilters(true);
    };

    restoreStoredFilters();

    return () => {
      isActive = false;
    };
  }, [calenderTypes, hasRestoredFilters, setValue]);

  useEffect(() => {
    if (!hasRestoredFilters) return;

    updateStoredCalendarFilters({
      calendarTypeId: selectedCalendarType?._id || "all",
      calendarSubTypeId: selectedCalendarSubType?._id || "all",
    });
  }, [hasRestoredFilters, selectedCalendarType, selectedCalendarSubType]);

  useEffect(() => {
    if (!activeCalendarPicker) return undefined;

    const handleOutsideClick = (event) => {
      const target = event.target;
      const clickedInsidePicker = target.closest(".calendar-title-dropdown");
      const clickedTitleButton = target.closest(
        ".fc-monthPicker-button, .fc-yearPicker-button",
      );

      if (!clickedInsidePicker && !clickedTitleButton) {
        setActiveCalendarPicker(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [activeCalendarPicker]);

  async function getCalenderTypes() {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/calendar-types`,
      );

      setCalenderTypes(response.data.data || []);
    } catch (err) {
      console.log("Error fetching calender types ", err);
      message.error("Failed to fetch calendar types");
    } finally {
      setLoading(false);
    }
  }

  async function fetchCalendarSub_Types(calendarTypeId) {
    if (!calendarTypeId) {
      setCalendarSubTypes([]);
      return [];
    }

    try {
      setLoading(true);

      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/Sub-Type/${calendarTypeId}?all=true`,
      );
      const subTypes = response?.data || [];
      setCalendarSubTypes(subTypes);
      return subTypes;
    } catch (err) {
      console.log("Error in fetching calendar sub types ", err);
      message.error("Failed to fetch calendar sub types");
      return [];
    } finally {
      setLoading(false);
    }
  }

  const getFormattedDate = (date) => {
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.slice(0, 10);
    }

    if (date instanceof Date && !isNaN(date)) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    }

    return date;
  };

  const getMonthDateRange = (date) => {
    const activeDate = new Date(date);

    if (isNaN(activeDate)) {
      return { monthStartDate: startDate, monthEndDate: endDate };
    }

    return {
      monthStartDate: new Date(
        activeDate.getFullYear(),
        activeDate.getMonth(),
        1,
      ),
      monthEndDate: new Date(
        activeDate.getFullYear(),
        activeDate.getMonth() + 1,
        0,
      ),
    };
  };

  async function getCalenderData() {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      params.append("loggedInUserId", loggedInUserId);

      if (startDate) {
        params.append("startDate", getFormattedDate(startDate));
      }

      if (endDate) {
        params.append("endDate", getFormattedDate(endDate));
      }

      if (selectedCalendarType?._id) {
        params.append("calendarTypeId", selectedCalendarType._id);
      }

      if (selectedCalendarSubType?._id) {
        params.append("calendarSubTypeId", selectedCalendarSubType._id);
      }

      const url = `${process.env.REACT_APP_BASE_URL}/calendarbooking/events?${params.toString()}`;

      const response = await ApiCallService.get(url);

      setAllCalendarData(response.data || []);
      setCalendarBackup(response.data || []);
    } catch (err) {
      console.log("Error in getCalendar Booking ", err);
      message.error("Failed to fetch calendar events");
    } finally {
      setLoading(false);
      setHasFetchedEvents(true);
    }
  }

  const handelCalenderTypeCLick = (selected) => {
    setSelectedCalendarType(selected || {});
    setSelectedCalendarSubType({});
    setValue("calendarType", selected?._id || "all");
    setValue("calendarSubType", "all");

    if (selected?._id) {
      fetchCalendarSub_Types(selected._id);
    } else {
      setCalendarSubTypes([]);
    }
  };

  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleDelete = async () => {
    if (!canDeleteEvent || !selectedEvent) return;
    handleMenuClose();
    handleOpenConfirm();
  };

  const handleMenuOpen = (event, calendarEvent) => {
    if (!canManageEvent) return;
    setAnchorEl(event.currentTarget);
    setSelectedEvent(calendarEvent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpdate = async () => {
    if (!selectedEvent) return;
    handleMenuClose();

    try {
      const updatedData = {
        heading: selectedEvent.heading + " (Updated)",
        subheading: selectedEvent.subheading,
        startDate: selectedEvent.startDate,
        endDate: selectedEvent.endDate,
        hyperlink: selectedEvent.hyperlink,
        calendarBookingType: selectedEvent.calendarBookingType,
        calendarTypeId: selectedEvent.calendarTypeId._id,
        calendarSubTypeId: selectedEvent.calendarSubTypeId?._id,
        documents: selectedEvent.documents,
      };

      await ApiCallService.put(
        `${process.env.REACT_APP_BASE_URL}/calendarbooking/${selectedEvent._id}/update`,
        updatedData,
      );

      message.success("Event updated successfully!");
      getCalenderData();
    } catch (error) {
      console.error(error);
      message.error("Failed to update event");
    }
  };

  const confirmDelete = async () => {
    if (!canDeleteEvent || !selectedEvent) return;
    handleCloseConfirm();

    try {
      setLoading(true);

      const response = await ApiCallService.patch(
        `${process.env.REACT_APP_BASE_URL}/calendarbooking/${selectedEvent._id}/deactivate`,
        { userId: loggedInUserId },
      );

      message.success(response.message);
      getCalenderData();
      handleCloseConfirm();
    } catch (error) {
      console.error("Error occured while deletion of the event", error);
      message.error("Failed to delete event");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";

    if (
      (typeof dateString === "string" && !dateString.includes("T")) ||
      (typeof dateString === "string" &&
        /T00:00:00(?:\.000)?Z$/.test(dateString))
    ) {
      return "";
    }

    const date = new Date(dateString);

    return date.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getEventDayKey = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const isMultiDayEvent = (event) =>
    Boolean(
      event?.startDate &&
      event?.endDate &&
      getEventDayKey(event.startDate) !== getEventDayKey(event.endDate),
    );

  const formatDateRange = (start, end) => {
    if (!start || !end) return formatDate(start || end);

    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const handleChange = () => {
    setExpanded((prev) => !prev);
  };

  const handleDownload = async (documentUrl) => {
    if (!canDownloadCalendarData) return;

    try {
      const response = await fetch(documentUrl);

      if (!response.ok) {
        message.warning("Download failed");
        throw new Error("Failed to fetch the image.");
      }

      const blob = await response.blob();
      message.success("Downloading started");

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      const filename = documentUrl.substring(documentUrl.lastIndexOf("/") + 1);

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      message.success("Downloading completed");
    } catch (error) {
      message.error("Downloading failed");
      console.error("Download failed:", error);
    }
  };

  const filterEventsByDate = (targetDate) => {
    const targetDateObj = new Date(targetDate);

    return allCalendarData.filter((event) => {
      const eventStartDate = new Date(event.startDate);
      return eventStartDate.toDateString() === targetDateObj.toDateString();
    });
  };

  const handleDateClick = (arg) => {
    const filteredData = filterEventsByDate(arg.dateStr);

    if (filteredData.length > 0) {
      setAllCalendarData(filteredData);
    } else {
      setAllCalendarData(calendarBackup);
      message.warning("No event found");
    }
  };

  const handleEventClick = (arg) => { };

  const handleViewChangeOnChange = (dateInfo) => {
    const activeDate =
      dateInfo.view?.currentStart ||
      dateInfo.view?.activeStart ||
      dateInfo.start;
    const { monthStartDate, monthEndDate } = getMonthDateRange(activeDate);
    const visibleDate = new Date(activeDate);

    setStartDate(getFormattedDate(monthStartDate));
    setEndDate(getFormattedDate(monthEndDate));
    setActiveCalendarPicker(null);

    if (!isNaN(visibleDate)) {
      setSelectedMonth(visibleDate.getMonth());
      setSelectedYear(visibleDate.getFullYear());
    }

    if (activeDate) {
      updateStoredCalendarFilters({
        activeDate: getFormattedDate(activeDate),
        calendarView: dateInfo.view?.type,
      });
    }
  };

  const mappedEvents = allCalendarData?.map((event) => {
    const CustomBorderColor = event?.calendarTypeId?.color;
    return {
      title: event.heading,
      start: event.startDate,
      end: event.endDate,
      url: event.hyperlink,
      backgroundColor: event?.calendarTypeId?.backgroundColor || "#808080",
      borderColor: event?.calendarTypeId?.color || "#808080",
    };
  });

  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startDayKey = getEventDayKey(start);
    const endDayKey = getEventDayKey(end);

    if (startDayKey && endDayKey && startDayKey !== endDayKey) {
      const startDay = new Date(`${startDayKey}T00:00:00`);
      const endDay = new Date(`${endDayKey}T00:00:00`);
      const diffInDays =
        Math.round((endDay - startDay) / (1000 * 60 * 60 * 24)) + 1;

      return `${diffInDays} Day${diffInDays > 1 ? "s" : ""} Event`;
    }

    const diffInMs = endDate - startDate;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInDays >= 1) {
      return `${Math.round(diffInDays)} Day${diffInDays > 1 ? "s" : ""} Event`;
    }

    return `${Math.round(diffInHours)} Hour${diffInHours > 1 ? "s" : ""} Event`;
  };

  const openDocument = (documentUrl) => {
    setSelectedDocument(documentUrl);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
  };

  const getDocumentType = (url) => {
    const extension = url.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) {
      return "image";
    }

    if (extension === "pdf") {
      return "pdf";
    }

    if (
      [
        "doc",
        "docx",
        "ppt",
        "pptx",
        "xls",
        "xlsx",
        "odt",
        "ods",
        "odf",
        "odp",
      ].includes(extension)
    ) {
      return "office";
    }

    if (
      [
        "mp4",
        "3gp",
        "avi",
        "mkv",
        "mov",
        "wmv",
        "flv",
        "mp3",
        "aac",
        "ogg",
        "wma",
        "flac",
        "m4a",
        "wav",
        "aiff",
        "amr",
      ].includes(extension)
    ) {
      return "video";
    }

    return "unknown";
  };

  const getFileName = (url) => {
    const fullFileName = url.split("/").pop();
    return fullFileName.substring(37);
  };

  const isRegisteredUser = !localStorage.getItem("userId");

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: CALENDAR_YEAR_RANGE_OFFSET * 2 + 1 },
    (_, index) => currentYear - CALENDAR_YEAR_RANGE_OFFSET + index,
  );

  const navigateToMonthYear = (month, year) => {
    const numericMonth = Number(month);
    const numericYear = Number(year);
    const targetDate = new Date(numericYear, numericMonth, 1);

    setSelectedMonth(numericMonth);
    setSelectedYear(numericYear);
    setActiveCalendarPicker(null);

    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(targetDate);
      return;
    }

    const { monthStartDate, monthEndDate } = getMonthDateRange(targetDate);
    setStartDate(getFormattedDate(monthStartDate));
    setEndDate(getFormattedDate(monthEndDate));
  };

  const handleMonthSelect = (month) => {
    navigateToMonthYear(month, selectedYear);
  };

  const handleYearSelect = (year) => {
    navigateToMonthYear(selectedMonth, year);
  };

  const toggleCalendarPicker = (picker) => {
    setActiveCalendarPicker((currentPicker) =>
      currentPicker === picker ? null : picker,
    );
  };

  const handleLike = (id) => {
    setLikedEvents((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getTodayLabel = () => {
    const today = new Date();

    const day = today.toLocaleDateString("en-US", { weekday: "long" });
    const date = today.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
    });

    return `${day} ${date}`;
  };

  const sanitizeExcelValue = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const getCalendarSubTypeName = (event) =>
    event?.calendarSubTypeId?.programId?.name ||
    event?.calendarSubTypeId?.name ||
    "";

  const handleDownloadExcel = () => {
    if (!canDownloadCalendarData) return;

    if (!allCalendarData?.length) {
      message.warning("No calendar information available to download");
      return;
    }

    const headers = [
      "Case / Event",
      "Description",
      "Event Type",
      "Sub Type",
      "Start Date",
      "Start Time",
      "End Date",
      "End Time",
      "Duration",
      "Meeting Type",
      "Meeting Address",
      "Meeting Link",
    ];

    const rows = allCalendarData.map((event) => [
      event.heading,
      event.subheading,
      event?.calendarTypeId?.name,
      getCalendarSubTypeName(event),
      formatDate(event.startDate),
      formatTime(event.startDate),
      formatDate(event.endDate),
      formatTime(event.endDate),
      event.endDate ? calculateDuration(event.startDate, event.endDate) : "",
      event.meetingType,
      event.meetingAddress,
      event.hyperlink,
      event.documents?.length || 0,
    ]);

    const tableRows = [headers, ...rows]
      .map(
        (row) =>
          `<tr>${row
            .map((cell) => `<td>${sanitizeExcelValue(cell)}</td>`)
            .join("")}</tr>`,
      )
      .join("");

    const worksheet = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>
        <body>
          <table border="1">
            ${tableRows}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob(["\uFEFF", worksheet], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const dateRange = [startDate, endDate].filter(Boolean).join("_to_");

    link.href = url;
    link.download = `calendar_information${dateRange ? `_${dateRange}` : ""}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success("Calendar information downloaded");
  };

  return (
    <div className="calendar-container">
      <Spinner loading={loading} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mb: 3,
        }}
      >
        {/* TOP CENTER HEADING */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            color: "#000",
            fontSize: { xs: "1.8rem", md: "2.2rem" },
          }}
        >
          Calendar
        </Typography>

        {/* SECOND LINE: FILTERS LEFT + ADD EVENT RIGHT */}
        {canUseCalendarToolbar && <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* LEFT FILTERS */}
          {canListEvents && <Box
            sx={{
              display: "flex",
              gap: 4,
              alignItems: "flex-start",
              flexDirection: { xs: "column", sm: "row" },
              width:"100%",
            }}
          >
            <Box sx={{display:"flex", flexDirection:{xs:"column", md:"row"}, justifyContent:"space-between", minWidth:"100%" }}>
              <CustomSelect
                name="calendarType"
                control={control}
                label="Event Type"
                sx={{width:{xs:"100%", md:"360px"}}}
                options={[
                  { value: "all", label: "All" },
                  ...[...calenderTypes]
                    .sort((firstType, secondType) => {
                      const isCaseCounselling = (type) =>
                        type.eventType?.trim().toUpperCase() === "OTHER" ||
                        ["CASE COUNSELLING", "CASE COUNCELLING"].includes(
                          type.name?.trim().toUpperCase(),
                        );

                      return (
                        Number(isCaseCounselling(firstType)) -
                        Number(isCaseCounselling(secondType))
                      );
                    })
                    .map((e) => ({
                      value: e._id,
                      label: e.name,
                      color:
                        e.eventType?.trim().toUpperCase() === "OTHER" ||
                          ["CASE COUNSELLING", "CASE COUNCELLING"].includes(
                            e.name?.trim().toUpperCase(),
                          )
                          ? undefined
                          : e.color,
                    })),
                ]}
                onChange={(e) => {
                  const selectedId = e.target.value;

                  if (selectedId === "all") {
                    handelCalenderTypeCLick({});
                  } else {
                    const selected = calenderTypes.find(
                      (c) => c._id === selectedId,
                    );

                    handelCalenderTypeCLick(selected);
                  }
                }}
              />
                {(canCreateEvent || canDownloadCalendarData) && <Box
                    sx={{
                    alignSelf: {xs:"flex-end", md:"center"},
                    display: "flex",
                    flexDirection:"row",
                    gap: 1,
                    flexWrap: "nowrap",
                    justifyContent: "flex-end",
                  }}
                >
                  {canDownloadCalendarData && (
                    <Tooltip title="Download the calendar data as an Excel file" arrow>
                      <span>
                        <Button
                          variant="contained"
                          color="customPink"
                          onClick={handleDownloadExcel}
                          disabled={!allCalendarData?.length}
                          aria-label="Download the calendar data as an Excel file"
                          sx={{
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            alignContent:"center",
                            textTransform: "none",
                            borderRadius: 1,
                            gap:0.5
                          }}
                        ><FileDownloadOutlinedIcon />
                          {isMobile ? "Excel" : "Download Excel"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {canCreateEvent && (<Button
                    variant="contained"
                    color="customPink"
                    onClick={() => navigate("/events/create")}
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: "fit-content",
                      gap:0.1
                    }}
                  >
                    <AddIcon />
                    {isMobile ? "Event" : "Add Event"}
                  </Button>)}
                </Box>
              }

              {calendarSubTypes.length > 0 && (
                <Box sx={{ width: { xs: "100%", sm: "260px" } }}>
                  <CustomSelect
                    name="calendarSubType"
                    control={control}
                    label="Sub Type"
                    fullWidth
                    options={[
                      { value: "all", label: "All" },
                      ...calendarSubTypes.map((e) => ({
                        value: e._id,
                        label:
                          e?.programId?.name || e?.name || "Unnamed Sub Type",
                      })),
                    ]}
                    onChange={(e) => {
                      const selectedId = e.target.value;

                      if (selectedId === "all") {
                        setSelectedCalendarSubType({});
                      } else {
                        const selected = calendarSubTypes.find(
                          (c) => c._id === selectedId,
                        );

                        setSelectedCalendarSubType(selected || {});
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>}
        </Box>
        }
      </Box>

      {canListEvents && <div className="calendar-content">
        <div className="calendar-events">
          {activeCalendarPicker && (
            <div className="calendar-title-dropdown">
              {activeCalendarPicker === "month" &&
                MONTH_OPTIONS.map((month, index) => (
                  <button
                    key={month}
                    type="button"
                    className={
                      selectedMonth === index
                        ? "calendar-title-option active"
                        : "calendar-title-option"
                    }
                    onClick={() => handleMonthSelect(index)}
                  >
                    {month}
                  </button>
                ))}

              {activeCalendarPicker === "year" &&
                yearOptions.map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={
                      selectedYear === year
                        ? "calendar-title-option active"
                        : "calendar-title-option"
                    }
                    onClick={() => handleYearSelect(year)}
                  >
                    {year}
                  </button>
                ))}
            </div>
          )}

          {allCalendarData && (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={initialCalendarDate}
              displayEventTime={false}
              eventDisplay="block"
              customButtons={{
                customToday: {
                  text: getTodayLabel(),
                  click: function () {
                    setActiveCalendarPicker(null);
                    document
                      .querySelector(".fc-today-button")
                      ?.dispatchEvent(new Event("click"));
                  },
                },
                monthPicker: {
                  text: MONTH_OPTIONS[selectedMonth],
                  click: function () {
                    toggleCalendarPicker("month");
                  },
                },
                yearPicker: {
                  text: String(selectedYear),
                  click: function () {
                    toggleCalendarPicker("year");
                  },
                },
              }}
              headerToolbar={
                isMobile
                  ? {
                    left: "",
                    center: "monthPicker yearPicker",
                    right: "",
                  }
                  : isTablet
                    ? {
                        left: "",
                        center: "prev monthPicker yearPicker next",
                        right: "dayGridMonth",
                      }
                    : {
                        left: "",
                        center: "prev monthPicker yearPicker next",
                        right: "dayGridMonth,timeGridWeek",
                  }
              }
              events={mappedEvents}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              editable={true}
              selectable={true}
              nowIndicator={true}
              dayMaxEventRows={2}
              height={isMobile ? "auto" : "80vh"}
              datesSet={(dateInfo) => handleViewChangeOnChange(dateInfo)}

            />
          )}
        </div>

        <div className="calendar-schedule">
          <div
            style={{
              height: "100%",
              width: "100%",
              backgroundColor: "white",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            {allCalendarData?.length > 0
              ? allCalendarData.map((e) => {
                const color = e?.calendarTypeId?.color || "#888";
                const customBackgroundColor =
                  e?.calendarTypeId?.backgroundColor || "#767676";
                const showDateRange = isMultiDayEvent(e);
                const startTimeLabel = formatTime(e.startDate);
                return (
                  <div
                    key={e._id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) 220px",
                      gap: "14px",
                      alignItems: "start",
                      borderLeft: `6px solid ${color}`,
                      border: "1px solid rgba(199, 199, 199, 0.52)",
                      background: "#fff",
                      borderRadius: "10px",
                      padding: "14px 18px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {/* LEFT SIDE */}
                    <div
                      style={{
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: { xs: "100px", sm: "120px", md: "220px" },
                          // minWidth: 0,
                        }}
                      >
                        {/* <span
        style={{
          width: "10px",
          height: "10px",
          background: color,
          borderRadius: "5%",
          flexShrink: 0,
        }}
      /> */}
                        <Tooltip
                          title={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                p: 1,
                                maxWidth: 360,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold", color: "#ffffff" }}
                              >
                                {e.heading}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#ffffff" }}
                              >
                                {e.subheading || "No description available"}
                              </Typography>
                            </Box>
                          }
                          interactive
                          placement="top"
                          enterDelay={150}
                          leaveDelay={100}
                        >
                          <div
                            tabIndex={0}
                            style={{
                              fontWeight: "bold",
                              color: "#000",
                              border: `2px solid ${color}`,
                              paddingTop: "4px",
                              paddingLeft: "8px",
                              paddingRight: "8px",
                              paddingBottom: "4px",
                              borderRadius: "4px",
                              background: customBackgroundColor,
                              fontSize: "16px",
                              lineHeight: "1.35",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                              cursor: "default",
                            }}
                            aria-label={`Event: ${e.heading}`}
                          >
                            {e.heading}
                          </div>
                        </Tooltip>
                      </Box>

                      {!isMobile && <div
                        style={{
                          fontSize: 14,
                          color: "#777",
                          marginTop: "6px",
                          lineHeight: "1.4",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {e.subheading || ""}
                      </div>}
                    </div>

                    {/* RIGHT SIDE */}
                    <div
                      style={{
                        width: "220px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "8px",
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                          flexWrap: "nowrap",
                          width: "100%",
                        }}
                      >
                        {canViewEvent && e.meetingType !== "Offline" && (
                          <Tooltip title="Meeting">
                            <IconButton
                              onClick={() => window.open(e.hyperlink)}
                              sx={{ cursor: "pointer", color: "#000" }}
                              size="small"
                            >
                              <DuoIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {<Tooltip title="Full View">
                          <IconButton
                            onClick={() =>
                              navigate(`/event-details/${e._id}`)
                            }
                            size="small"
                            sx={{ color: "#000" }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        }

                        {e.documents?.length > 0 && (
                          <Tooltip
                            title={`${e.documents.length} document(s) available`}
                          >
                            <IconButton size="small">
                              <Badge
                                badgeContent={e.documents.length}
                                style={{ color: e?.calendarTypeId?.color }}
                              >
                                <AttachFileIcon
                                  style={{ color: e?.calendarTypeId?.color }}
                                />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        )}

                        {canManageEvent && <Tooltip title="More Options">
                          <IconButton
                            onClick={(ev) => handleMenuOpen(ev, e)}
                            size="small"
                            sx={{ color: "#000" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>}
                      </div>

                      {e.meetingAddress?.trim() && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#555",
                            textAlign: "right",
                            lineHeight: "1.35",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={e.meetingAddress}
                        >
                          📍 {e.meetingAddress}
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "8px",
                          width: "100%",
                          flexWrap: "wrap",
                          textAlign: "right",
                        }}
                      >
                        <span
                          style={{
                            //fontWeight: "600",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          {showDateRange
                            ? formatDateRange(e.startDate, e.endDate)
                            : formatDate(e.startDate)}
                        </span>

                      </div>
                    </div>
                  </div>
                );
              })
              : !loading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "50px 20px",
                    background: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    maxWidth: "400px",
                    margin: "40px auto",
                    transition: "0.3s",
                  }}
                >
                  <div
                    style={{
                      fontSize: "70px",
                      animation: "float 3s ease-in-out infinite",
                    }}
                  >
                    📅
                  </div>

                  <h3 style={{ marginTop: "15px", color: "#333" }}>
                    No Events Found
                  </h3>

                  <p style={{ color: "#888", fontSize: "14px" }}>
                    Try changing filters or create a new event.
                  </p>

                  <style>
                    {`
                    @keyframes float {
                      0% { transform: translateY(0px); }
                      50% { transform: translateY(-15px); }
                      100% { transform: translateY(0px); }
                    }
                  `}
                  </style>
                </div>
              )}
          </div>
        </div>
      </div>}

      {isViewerOpen && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 relative w-[90%] h-[90%] max-w-4xl max-h-[90vh] overflow-hidden rounded-lg shadow-lg flex flex-col">
            <IconButton
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                color: "red",
                zIndex: 2000,
              }}
              onClick={closeViewer}
            >
              <Close style={{ fontSize: "28px" }} />
            </IconButton>

            <div className="flex-1 w-full h-full flex justify-center items-center">
              {getDocumentType(selectedDocument) === "pdf" && (
                <iframe
                  src={selectedDocument}
                  title="Document Viewer"
                  className="w-full h-full"
                  onLoad={() => setLoading(false)}
                ></iframe>
              )}

              {getDocumentType(selectedDocument) === "image" && (
                <img
                  src={selectedDocument}
                  alt="Document Preview"
                  className="w-full h-full object-contain"
                  onLoad={() => setLoading(false)}
                />
              )}

              {getDocumentType(selectedDocument) === "office" && (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    selectedDocument,
                  )}`}
                  title="Document Viewer"
                  className="w-full h-full border-none"
                  onLoad={() => setLoading(false)}
                ></iframe>
              )}

              {getDocumentType(selectedDocument) === "video" && (
                <ReactPlayer
                  url={selectedDocument}
                  controls
                  playing
                  className="w-full h-full"
                  onReady={() => setLoading(false)}
                />
              )}

              {getDocumentType(selectedDocument) === "unknown" && (
                <div className="flex flex-col items-center justify-center w-full h-full bg-gray-100 rounded-md shadow p-6">
                  <p className="text-gray-600 text-lg font-medium">
                    Cannot preview this document type
                  </p>

                  {canDownloadCalendarData && <Button
                    className="mt-4 px-6 py-2 text-lg font-bold bg-blue-500 text-white rounded-lg hover:scale-105 transition-transform"
                    variant="contained"
                    onClick={() => handleDownload(selectedDocument)}
                  >
                    Download
                  </Button>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {canManageEvent && <Menu
        id="event-actions-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            borderRadius: 2,
            mt: 1,
            bgcolor: "#ffffff",
            border: "1px solid #090a0cff",
            "& .MuiMenuItem-root": {
              borderRadius: 1,
              transition: "all 0.2s ease",
            },
            "& .MuiMenuItem-root:hover": {
              bgcolor: "#f3f4f6",
              transform: "translateX(4px)",
            },
          },
        }}
      >
        {canEditEvent && <MenuItem
          onClick={() => {
            navigate(`/events/${selectedEvent._id}/edit`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon sx={{ color: "#6c27b3" }} fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Update Event"
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500,
              color: "#6c27b3",
            }}
          />
        </MenuItem>}

        {canEditEvent && canDeleteEvent && <Divider />}

        {/* <MenuItem
          onClick={() => {
            navigate(`/events/${selectedEvent._id}/history`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <HistoryIcon sx={{ color: "#1f6feb" }} fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="View History"
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500,
              color: "#1f6feb",
            }}
          />
        </MenuItem> */}

        {canDeleteEvent && <MenuItem
          onClick={() => {
            handleDelete(selectedEvent);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon sx={{ color: "#e53935" }} fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Delete Event"
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500,
              color: "#e53935",
            }}
          />
        </MenuItem>}
      </Menu>}

      {canDeleteEvent && selectedEvent && (
        <Dialog
          open={openConfirm}
          onClose={handleCloseConfirm}
          PaperProps={{
            sx: {
              borderRadius: 3,
              padding: 1,
              width: 400,
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              color: "#d32f2f",
            }}
          >
            Confirm Deletion
          </DialogTitle>

          <DialogContent>
            <Typography
              sx={{ textAlign: "center", fontSize: 16, color: "#555" }}
            >
              Are you sure you want to delete this event?
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
            <Button
              onClick={handleCloseConfirm}
              variant="contained"
              sx={{
                bgcolor: "#e0e0e0",
                color: "#333",
                "&:hover": { bgcolor: "#cfcfcf" },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={confirmDelete}
              variant="contained"
              sx={{
                bgcolor: "#d32f2f",
                "&:hover": { bgcolor: "#b71c1c" },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default Calendar;
